// components/RecoveryPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { resetPasswordWithRecovery } from '../utils/crypto';
import RecoveryKeyDisplay from './RecoveryKeyDisplay';
import { 
  KeyRound, 
  Lock, 
  Mail, 
  AlertCircle, 
  Shield, 
  Eye, 
  EyeOff,
  CheckCircle,
  ArrowLeft,
  Bug,
  BugOff
} from 'lucide-react';

const RecoveryPage = ({ 
  projectId,
  projectName = 'App',
  loginPath = '/login',
  apiUrl = 'http://localhost:8000',
  brandColor = 'slate'
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState('email');
  const [email, setEmail] = useState('');
  const [recoveryKey, setRecoveryKey] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [encryptionData, setEncryptionData] = useState(null);
  const [newRecoveryKey, setNewRecoveryKey] = useState('');
  
  // Debug mode toggle
  const [debugMode, setDebugMode] = useState(true);
  const [debugLogs, setDebugLogs] = useState([]);

  // Color scheme
  const colorSchemes = {
    slate: {
      gradient: 'from-slate-900 via-slate-800 to-slate-900',
      button: 'from-slate-800 to-slate-900 hover:from-slate-700 hover:to-slate-800',
      ring: 'focus:ring-slate-500',
      text: 'text-slate-700 hover:text-slate-900',
      lightBg: 'bg-slate-50',
      iconColor: 'text-slate-600'
    },
    blue: {
      gradient: 'from-blue-900 via-blue-800 to-blue-900',
      button: 'from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600',
      ring: 'focus:ring-blue-500',
      text: 'text-blue-600 hover:text-blue-800',
      lightBg: 'bg-blue-50',
      iconColor: 'text-blue-600'
    },
    green: {
      gradient: 'from-green-900 via-green-800 to-green-900',
      button: 'from-green-600 to-green-700 hover:from-green-500 hover:to-green-600',
      ring: 'focus:ring-green-500',
      text: 'text-green-600 hover:text-green-800',
      lightBg: 'bg-green-50',
      iconColor: 'text-green-600'
    },
    purple: {
      gradient: 'from-purple-900 via-purple-800 to-purple-900',
      button: 'from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600',
      ring: 'focus:ring-purple-500',
      text: 'text-purple-600 hover:text-purple-800',
      lightBg: 'bg-purple-50',
      iconColor: 'text-purple-600'
    }
  };

  const colors = colorSchemes[brandColor] || colorSchemes.slate;

  // Debug logging function
  const debugLog = (message, data = null, type = 'info') => {
    const timestamp = new Date().toISOString();
    const logEntry = { timestamp, message, data, type };
    
    if (debugMode) {
      const styles = {
        info: 'color: #3B82F6',
        success: 'color: #10B981',
        error: 'color: #EF4444',
        warning: 'color: #F59E0B',
        data: 'color: #8B5CF6'
      };
      
      console.log(`%c[${timestamp}] ${message}`, styles[type] || styles.info);
      if (data) {
        console.log('Data:', data);
      }
      
      setDebugLogs(prev => [...prev.slice(-19), logEntry]);
    }
  };

  const clearDebugLogs = () => {
    setDebugLogs([]);
    console.clear();
  };

  useEffect(() => {
    debugLog('RecoveryPage component mounted', {
      projectId,
      apiUrl,
      currentStep
    });
  }, []);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return null;
  };

  const validatePassword = (password) => {
    if (password.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
    if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
    if (!/[0-9]/.test(password)) return 'Password must contain at least one number';
    return null;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    debugLog('Email submission started', { email });
    
    const emailError = validateEmail(email);
    if (emailError) {
      debugLog('Email validation failed', { error: emailError }, 'error');
      setError(emailError);
      return;
    }
    
    setLoading(true);
    debugLog('Clearing session storage');
    sessionStorage.clear();

    try {
      const endpoint = `${apiUrl}/auth/encryption-data?email=${encodeURIComponent(email)}`;
      
      debugLog('Using GET endpoint', {
        endpoint,
        method: 'GET'
      }, 'data');

      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      debugLog('Response received', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      }, response.ok ? 'success' : 'error');

      let responseData = null;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        try {
          responseData = await response.json();
          debugLog('Response data parsed', responseData, 'data');
          
          if (responseData?.detail && Array.isArray(responseData.detail)) {
            debugLog('Validation errors detected', {
              errors: responseData.detail.map(err => ({
                location: err.loc,
                message: err.msg,
                type: err.type,
                context: err.ctx
              }))
            }, 'error');
          }
        } catch (parseError) {
          debugLog('Failed to parse response JSON', { parseError }, 'error');
        }
      } else {
        debugLog('Response is not JSON', { contentType }, 'warning');
        const textData = await response.text();
        debugLog('Response text', { textData }, 'data');
      }

      if (!response.ok) {
        if (response.status === 422) {
          debugLog('422 Unprocessable Entity', {
            expectedFormat: 'Likely expecting different field names or structure',
            sentData: { email },
            receivedError: responseData
          }, 'error');
          
          setError('Server validation error. Please check the request format.');
          setLoading(false);
          return;
        }
        
        if (response.status === 404) {
          debugLog('404 - Email might not exist', null, 'warning');
          setError('If an account exists with this email, please proceed with your recovery key.');
          setCurrentStep('recovery');
          return;
        }
        
        throw new Error(responseData?.detail || responseData?.message || 'Failed to process request');
      }

      debugLog('Request successful, checking encryption data', responseData, 'success');
      
      if (responseData && responseData.salt && responseData.encrypted_symmetric_key_with_recovery) {
        debugLog('Encryption data received', {
          hasData: true,
          fields: Object.keys(responseData)
        }, 'success');
        setEncryptionData(responseData);
        setCurrentStep('recovery');
      } else {
        debugLog('No encryption data in response', { responseData }, 'warning');
        setError('If an account exists with this email, please proceed with your recovery key.');
        setCurrentStep('recovery');
      }
      
    } catch (err) {
      debugLog('Caught error in handleEmailSubmit', {
        error: err.message,
        stack: err.stack,
        name: err.name
      }, 'error');
      
      if (err.name === 'TypeError' && err.message === 'Failed to fetch') {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError('If an account exists with this email, please proceed with your recovery key.');
        setCurrentStep('recovery');
      }
    } finally {
      setLoading(false);
      debugLog('Email submission completed', { currentStep });
    }
  };

  const handleRecoveryKeySubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    debugLog('Recovery key submission started', {
      keyLength: recoveryKey.length,
      keyPreview: recoveryKey.substring(0, 10) + '...',
      hasEncryptionData: !!encryptionData
    });
    
    if (!recoveryKey || recoveryKey.trim().length < 10) {
      debugLog('Recovery key validation failed - too short', null, 'error');
      setError('Please enter a valid recovery key');
      return;
    }
    
    if (!encryptionData || Object.keys(encryptionData).length === 0) {
      debugLog('No encryption data available', { encryptionData }, 'error');
      setError('Unable to proceed. Please start over and ensure you enter the correct email.');
      return;
    }
    
    debugLog('Encryption data available', {
      fields: Object.keys(encryptionData),
      salt: encryptionData.salt ? 'Present' : 'Missing',
      encryptedSymmetricKeyWithRecovery: encryptionData.encrypted_symmetric_key_with_recovery ? 'Present' : 'Missing',
      encryptedPrivateKey: encryptionData.encrypted_private_key ? 'Present' : 'Missing'
    }, 'success');
    
    setCurrentStep('newPassword');
  };

  const handleNewPasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    debugLog('Password reset started', {
      email,
      newPasswordLength: newPassword.length,
      passwordsMatch: newPassword === confirmPassword
    });

    if (newPassword !== confirmPassword) {
      debugLog('Passwords do not match', null, 'error');
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      debugLog('Password validation failed', { error: passwordError }, 'error');
      setError(passwordError);
      setLoading(false);
      return;
    }

    try {
      debugLog('Attempting client-side decryption with recovery key', {
        recoveryKeyLength: recoveryKey.trim().length,
        encryptionDataFields: Object.keys(encryptionData)
      });
      
      const resetResult = await resetPasswordWithRecovery(
        recoveryKey.trim(),
        newPassword,
        encryptionData
      );

      debugLog('Client-side reset result', {
        success: resetResult.success,
        hasEncryptedPrivateKey: !!resetResult.encrypted_private_key,
        hasEncryptedSymmetricKey: !!resetResult.encrypted_symmetric_key,
        hasNewRecoveryKey: !!resetResult.newRecoveryKey,
        error: resetResult.error
      }, resetResult.success ? 'success' : 'error');

      if (!resetResult.success) {
        throw new Error(resetResult.error || 'Invalid recovery key. Please check and try again.');
      }

      // Store the new recovery key to show to user
      if (resetResult.newRecoveryKey) {
        debugLog('New recovery key generated', {
          keyLength: resetResult.newRecoveryKey.length
        }, 'success');
        setNewRecoveryKey(resetResult.newRecoveryKey);
      }

      const endpoint = `${apiUrl}/auth/reset-password`;
      const requestBody = {
        email: email,
        recovery_key: recoveryKey.trim(),
        new_password: newPassword,
        encrypted_private_key: resetResult.encrypted_private_key,
        encrypted_symmetric_key: resetResult.encrypted_symmetric_key,
        encrypted_symmetric_key_with_recovery: resetResult.encrypted_symmetric_key_with_recovery,
        recovery_key_hash: resetResult.recovery_key_hash,
        salt: resetResult.salt,
        project_id: projectId
      };

      debugLog('Sending password reset to backend', {
        endpoint,
        bodyFields: Object.keys(requestBody),
        hasProjectId: !!projectId
      }, 'data');

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      debugLog('Backend response', {
        status: response.status,
        ok: response.ok
      }, response.ok ? 'success' : 'error');

      const responseData = await response.json();
      debugLog('Backend response data', responseData, 'data');

      if (!response.ok) {
        if (response.status === 401 && responseData.detail?.includes('Invalid recovery key')) {
          throw new Error('Invalid recovery key. Please check your recovery key and try again.');
        }
        throw new Error(responseData.detail || 'Failed to reset password');
      }
      
      if (responseData.success) {
        debugLog('Password reset successful!', null, 'success');
        setCurrentStep('showRecoveryKey');
      } else {
        throw new Error(responseData.message || 'Password reset failed');
      }

    } catch (err) {
      debugLog('Password reset error', {
        error: err.message,
        stack: err.stack
      }, 'error');
      
      if (err.name === 'TypeError' && err.message === 'Failed to fetch') {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(err.message || 'Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRecoveryKeySaved = () => {
    debugLog('User confirmed recovery key saved, navigating to login');
    navigate(loginPath);
  };

  // Debug panel component
  const DebugPanel = () => {
    if (!debugMode) return null;
    
    return (
      <div className="fixed bottom-4 right-4 w-96 max-h-96 bg-black bg-opacity-90 text-green-400 p-4 rounded-lg overflow-auto font-mono text-xs z-50">
        <div className="flex justify-between items-center mb-2">
          <span className="text-yellow-400 font-bold">Debug Console</span>
          <button
            onClick={clearDebugLogs}
            className="text-red-400 hover:text-red-300"
          >
            Clear
          </button>
        </div>
        <div className="space-y-1">
          {debugLogs.map((log, index) => (
            <div key={index} className={`
              ${log.type === 'error' ? 'text-red-400' : ''}
              ${log.type === 'warning' ? 'text-yellow-400' : ''}
              ${log.type === 'success' ? 'text-green-400' : ''}
              ${log.type === 'data' ? 'text-purple-400' : ''}
            `}>
              <div>[{new Date(log.timestamp).toLocaleTimeString()}] {log.message}</div>
              {log.data && (
                <div className="ml-4 text-gray-400">
                  {JSON.stringify(log.data, null, 2)}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Step progress indicator
  const ProgressIndicator = () => {
    const steps = [
      { id: 'email', label: 'Email', icon: Mail },
      { id: 'recovery', label: 'Recovery Key', icon: KeyRound },
      { id: 'newPassword', label: 'New Password', icon: Lock }
    ];

    const currentIndex = steps.findIndex(s => s.id === currentStep);

    return (
      <div className="flex items-center justify-center mb-8">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isActive = index <= currentIndex;
          const isCurrent = step.id === currentStep;

          return (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center">
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center transition-all
                  ${isActive 
                    ? `bg-gradient-to-r ${colors.button} text-white` 
                    : 'bg-gray-200 text-gray-500'
                  }
                  ${isCurrent ? 'ring-4 ring-opacity-30 ' + colors.ring : ''}
                `}>
                  <Icon className="w-5 h-5" />
                </div>
                <span className={`text-xs mt-2 ${isActive ? 'text-gray-700 font-semibold' : 'text-gray-500'}`}>
                  {step.label}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div className={`
                  w-20 h-1 mx-2 transition-all
                  ${index < currentIndex 
                    ? `bg-gradient-to-r ${colors.button}` 
                    : 'bg-gray-200'
                  }
                `} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  // Show new recovery key screen
  if (currentStep === 'showRecoveryKey' && newRecoveryKey) {
    return (
      <>
        <DebugPanel />
        <RecoveryKeyDisplay
          recoveryKey={newRecoveryKey}
          projectName={projectName}
          userEmail={email}
          onConfirm={handleRecoveryKeySaved}
          confirmButtonText="I have saved my new recovery key - Continue to Login"
          brandColor={brandColor}
          showWarning={true}
          requireConfirmation={true}
        />
      </>
    );
  }

  // Success screen (if somehow we skip the recovery key display)
  if (currentStep === 'success') {
    return (
      <>
        <DebugPanel />
        <div className={`min-h-screen bg-gradient-to-br ${colors.gradient} flex items-center justify-center p-4`}>
          <div className="max-w-md w-full">
            <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Password Reset Successful!
              </h2>
              <p className="text-gray-600 mb-6">
                Your password has been reset and your keys have been re-encrypted with your new password.
              </p>
              <button
                onClick={() => navigate(loginPath)}
                className={`w-full bg-gradient-to-r ${colors.button} text-white py-3 px-6 rounded-xl font-semibold shadow-lg transition-all duration-200 transform hover:scale-105`}
              >
                Sign In with New Password
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <DebugPanel />
      
      {/* Debug Toggle Button */}
      <button
        onClick={() => setDebugMode(!debugMode)}
        className="fixed top-4 right-4 p-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 z-50"
        title="Toggle Debug Mode"
      >
        {debugMode ? <BugOff className="w-5 h-5" /> : <Bug className="w-5 h-5" />}
      </button>

      <div className={`min-h-screen bg-gradient-to-br ${colors.gradient} flex items-center justify-center p-4`}>
        <div className="max-w-md w-full">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-block bg-white p-4 rounded-2xl shadow-lg mb-4">
              <div className="text-4xl font-bold text-gray-900">{projectName}</div>
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">Account Recovery</h2>
            <p className="text-gray-300">Reset your password using your recovery key</p>
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            {/* Back button */}
            <button
              onClick={() => navigate(loginPath)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
              disabled={loading}
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Back to login</span>
            </button>

            {/* Progress Indicator */}
            <ProgressIndicator />

            {/* Step 1: Email */}
            {currentStep === 'email' && (
              <form onSubmit={handleEmailSubmit} className="space-y-5">
                <div className={`${colors.lightBg} rounded-xl p-4 border border-gray-100`}>
                  <div className="flex items-start gap-3">
                    <Shield className={`w-5 h-5 ${colors.iconColor} mt-0.5`} />
                    <div className="text-sm">
                      <p className="font-semibold text-gray-900 mb-1">Recovery Key Required</p>
                      <p className="text-gray-600">
                        You'll need your recovery key that was provided during registration to reset your password.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                    <Mail className="w-4 h-4" />
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError('');
                      debugLog('Email input changed', { value: e.target.value });
                    }}
                    required
                    disabled={loading}
                    className={`w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 ${colors.ring} focus:border-transparent transition-all disabled:bg-gray-50`}
                    placeholder="you@example.com"
                    autoComplete="email"
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !email}
                  className={`w-full bg-gradient-to-r ${colors.button} text-white py-3 px-6 rounded-xl font-semibold shadow-lg transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
                >
                  {loading ? 'Loading...' : 'Continue'}
                </button>
              </form>
            )}

            {/* Step 2: Recovery Key */}
            {currentStep === 'recovery' && (
              <form onSubmit={handleRecoveryKeySubmit} className="space-y-5">
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                    <KeyRound className="w-4 h-4" />
                    Recovery Key
                  </label>
                  <textarea
                    value={recoveryKey}
                    onChange={(e) => {
                      setRecoveryKey(e.target.value);
                      setError('');
                      debugLog('Recovery key input changed', { 
                        length: e.target.value.length,
                        preview: e.target.value.substring(0, 10) + '...'
                      });
                    }}
                    required
                    disabled={loading}
                    rows={3}
                    className={`w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 ${colors.ring} focus:border-transparent transition-all disabled:bg-gray-50 font-mono text-sm`}
                    placeholder="Enter your recovery key"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    This key was provided when you created your account
                  </p>
                </div>

                {error && (
                  <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !recoveryKey}
                  className={`w-full bg-gradient-to-r ${colors.button} text-white py-3 px-6 rounded-xl font-semibold shadow-lg transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
                >
                  Verify Recovery Key
                </button>
                
                <button
                  type="button"
                  onClick={() => {
                    setCurrentStep('email');
                    debugLog('Navigating back to email step');
                  }}
                  className="w-full text-gray-600 hover:text-gray-900 py-2 transition-colors"
                >
                  Back to Email
                </button>
              </form>
            )}

            {/* Step 3: New Password */}
            {currentStep === 'newPassword' && (
              <form onSubmit={handleNewPasswordSubmit} className="space-y-5">
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                    <Lock className="w-4 h-4" />
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        setError('');
                      }}
                      required
                      disabled={loading}
                      minLength={8}
                      className={`w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 ${colors.ring} focus:border-transparent transition-all disabled:bg-gray-50`}
                      placeholder="Create a strong password"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      disabled={loading}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Must be 8+ characters with uppercase, lowercase, and number
                  </p>
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                    <Lock className="w-4 h-4" />
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        setError('');
                      }}
                      required
                      disabled={loading}
                      className={`w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 ${colors.ring} focus:border-transparent transition-all disabled:bg-gray-50`}
                      placeholder="Confirm your new password"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      disabled={loading}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full bg-gradient-to-r ${colors.button} text-white py-3 px-6 rounded-xl font-semibold shadow-lg transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Resetting Password...
                    </span>
                  ) : (
                    'Reset Password'
                  )}
                </button>
                
                <button
                  type="button"
                  onClick={() => setCurrentStep('recovery')}
                  className="w-full text-gray-600 hover:text-gray-900 py-2 transition-colors"
                  disabled={loading}
                >
                  Back to Recovery Key
                </button>
              </form>
            )}

            {/* Alternative Options */}
            {currentStep === 'email' && (
              <div className="mt-6 pt-6 border-t border-gray-100">
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-3">
                    Don't have your recovery key?
                  </p>
                  <p className="text-xs text-gray-500">
                    Unfortunately, without your recovery key, password reset is not possible due to end-to-end encryption. 
                    Contact support if you need assistance.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-400">
              Your data remains encrypted throughout this process
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default RecoveryPage;